import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { animations, colors } from '../../core/styles/theme/default';

const StyledGradientWrapper = styled.div<{
  showLeft: boolean;
  showRight: boolean;
}>(
  ({ showLeft, showRight }) => css`
    position: relative;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      width: ${rem(48)};
      height: 100%;
      pointer-events: none;
      z-index: 1;
      transition: opacity ${animations.default};
    }

    &::before {
      left: 0;
      background: linear-gradient(to left, transparent, white);
      opacity: ${showLeft ? 1 : 0};
    }

    &::after {
      right: 0;
      background: linear-gradient(to right, transparent, white);
      opacity: ${showRight ? 1 : 0};
    }
  `,
);

const StyledTableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${colors.white};

  th,
  td {
    text-align: left;
    font-size: ${rem(14)};
    min-width: ${rem(160)};
    padding: ${rem(8)};
    border: ${rem(1)} solid ${colors.grey400};

    * {
      font-size: inherit;
    }
  }

  tbody tr:nth-of-type(odd) {
    background: ${colors.grey200};
  }
`;

const Table = ({ children, className }: TableProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const wrapper = scrollRef.current;

    const updateGradients = () => {
      if (!wrapper) return;
      const { scrollLeft, scrollWidth, clientWidth } = wrapper;

      const isScrollable = scrollWidth > clientWidth;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setShowLeft(isScrollable && !atStart);
      setShowRight(isScrollable && !atEnd);
    };

    updateGradients();
    wrapper?.addEventListener('scroll', updateGradients);
    window.addEventListener('resize', updateGradients);

    return () => {
      wrapper?.removeEventListener('scroll', updateGradients);
      window.removeEventListener('resize', updateGradients);
    };
  }, []);

  return (
    <StyledGradientWrapper
      showLeft={showLeft}
      showRight={showRight}
      {...(className ? { className } : {})}
    >
      <StyledTableWrapper ref={scrollRef}>
        <StyledTable>{children}</StyledTable>
      </StyledTableWrapper>
    </StyledGradientWrapper>
  );
};

export type TableProps = {
  children: ReactNode;
  className?: string;
};

export default Table;
